/* eslint-disable no-unused-vars, jsx-a11y/alt-text */
import React, { useState, useMemo } from "react";
import debounce from "lodash/debounce";
import HeaderInfo from "../components/common/HeaderInfo";
import DataTable from "../components/common/DataTable";
import RequestDetailsModal from "../components/modals/RequestDetailsModal";
import CentralApproveModal from "../components/modals/CentralApproveModal";
import IssuedStockReportModal from "../components/modals/IssuedStockReportModal";
import { ClothIcon } from "../components/icons";
import { Check, X as XIcon, FileText } from "lucide-react";
import { toast } from "react-hot-toast";
import {
  useGetCentralRequestsQuery,
  useGetCampusRequestsQuery,
  useLazyGetSingleRequestQuery,
  useCentralApprovalRequestMutation,
  useGetCampusesQuery,
  useGetMeQuery,
} from "../store/api";
export default function Requests() {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("ALL");
  const [limit, setLimit] = useState(10);
  const [requestedCampusId, setRequestedCampusId] = useState("");
  const [centralApproveOpen, setCentralApproveOpen] = useState(false);
  const [approveRow, setApproveRow] = useState(null);

  const { data: userData, isLoading: userLoading } = useGetMeQuery();
  const allowedRoutes = userData?.data?.allowedRoutes || [];
  const isCentral = allowedRoutes.includes("all_requests") || allowedRoutes.includes("all");

  const { data: campusesData } = useGetCampusesQuery(1, { skip: !isCentral });
  const campusOptions = campusesData?.data?.campuses || [];

  const { data: centralData, isLoading: centralLoading } = useGetCentralRequestsQuery(
    { page, limit, search, status, requestedCampusId },
    { skip: !isCentral || userLoading }
  );

  const { data: campusData, isLoading: campusLoading } = useGetCampusRequestsQuery(
    { page, limit, search, status },
    { skip: isCentral || userLoading }
  );

  const activeData = isCentral ? centralData : campusData;
  const isLoading = isCentral ? centralLoading : campusLoading;

  const [getSingleRequest, { isLoading: detailsLoading }] =
    useLazyGetSingleRequestQuery();
  const [centralApprovalRequest, { isLoading: isCentralApproving }] = useCentralApprovalRequestMutation();

  const rows = activeData?.data?.requests || [];
  const totalPages = activeData?.data?.totalPages || 1;
  function handleNext() {
    if (page < totalPages) setPage((p) => p + 1);
  }

  function handlePrev() {
    if (page > 1) setPage((p) => p - 1);
  }

  const handleRejectCentral = async (r) => {
    try {
      const response = await centralApprovalRequest({
        requestId: r._id,
        status: "rejected_central",
        modifiedQuantity: 0,
      }).unwrap();
      if (response?.success === false) {
        toast.error(response?.message || "Failed to reject request");
        return;
      }
      toast.success("Request rejected successfully");
    } catch (error) {
      console.error(error);
      toast.error(error?.data?.message || "Failed to reject request");
    }
  };

  const handleApproveCentral = (r) => {
    setApproveRow(r);
    setCentralApproveOpen(true);
  };

  React.useEffect(() => {
    setPage(1);
  }, [search, status, limit, requestedCampusId]);
  const getStatusClass = (status) => {
    switch (status) {
      case "pending_vp":
      case "pending_central":
        return "bg-[#F6E8AD] text-[#986012] px-[19px]";

      case "approved_central":
        return "bg-[#E5FFF6] text-[#2C765C] px-4";

      case "recieved":
        return "bg-[#D6EAFE] text-[#0052CC] px-6";

      case "rejected_vp":
      case "rejected_central":
        return "bg-[#FFBFBF] text-[#F6200D] px-5";

      default:
        return "bg-[#EAEAEA] text-[#7A7A7A] px-5";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "pending_vp": return "Pending VP";
      case "pending_central": return "Pending Central";
      case "approved_central": return "Approved / Dispatch";
      case "recieved": return "Received";
      case "rejected_vp": return "Rejected (VP)";
      case "rejected_central": return "Rejected (Central)";
      default: return status;
    }
  };

  const debouncedSearch = useMemo(() => {
    return debounce((value) => {
      setSearch(value);
      setPage(1);
    }, 500);
  }, []);

  const renderRow = (r, idx) => (
    <div
      key={idx}
      className="bg-[#EEEEEE40]"
    >
      <div className="grid grid-cols-12 gap-4 px-7 py-6 items-center border-b border-[#E5E5E5]">
        <div className="col-span-2 flex items-center gap-2 text-black">
          <div className="w-[37px] h-[37px] flex items-center justify-center rounded-full bg-[#6D4E8A]">
            {/* <ClothIcon /> */}
            <img
              src={
                r?.centralCampusInventoryItemId?.itemId?.itemPicture?.secure_url
              }
            />
          </div>
          <div>
            <div className="leading-5">
              {r?.centralCampusInventoryItemId?.itemId?.name}
            </div>
            <div className="text-xs leading-3 text-[#A3A3A3] capitalized">
              REQ: {r._id?.slice(0, 8).toUpperCase()}
            </div>
          </div>
        </div>

        <div className="col-span-2 flex flex-col gap-1 text-center">
          <div className="text-black">
            {r.requestedQty} Requested
          </div>
          {r.status !== "pending_vp" && r.status !== "rejected_vp" && (
            <div className="text-[#878686] text-sm">
              {r.requestedQty - (r.vpModifiedQty || 0)} VP Approved
            </div>
          )}
          {r.issuedQty > 0 && (
            <div className="text-[#878686] text-sm">
              {r.issuedQty} to be issued
            </div>
          )}
        </div>

        <div className="col-span-3 flex justify-center">
          <p
            className={`border rounded-[10px] py-0.5 text-center font-medium text-xs ${getStatusClass(r.status)}`}
          >
            {getStatusText(r.status)}
          </p>
        </div>

        <div className="col-span-2 text-center">
          <p className="text-black  leading-[31px]">
            {" "}
            {new Intl.DateTimeFormat("en-GB", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            }).format(new Date(r.updatedAt))}
          </p>
          <p className="text-[#878686] leading-5">
            Updated:{" "}
            {new Date(r.updatedAt).toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            })}
          </p>
        </div>

        <div className="col-span-3 flex justify-center items-center gap-2">
          {r.status === "pending_central" && isCentral && (
            <>
              <button
                onClick={() => handleApproveCentral(r)}
                title="Approve"
                className="shrink-0 w-[30px] h-[30px] bg-[#E5FFF6] rounded-[10px] flex items-center justify-center border border-[#2C765C] text-[#2C765C] hover:bg-[#2C765C] hover:text-white transition-colors"
              >
                <Check size={16} strokeWidth={3} />
              </button>
              <button
                onClick={() => handleRejectCentral(r)}
                title="Reject"
                className="shrink-0 w-[30px] h-[30px] bg-[#FFBFBF] rounded-[10px] flex items-center justify-center border border-[#F6200D] text-[#F6200D] hover:bg-[#F6200D] hover:text-white transition-colors"
              >
                <XIcon size={16} strokeWidth={3} />
              </button>
            </>
          )}
          <button
            aria-label="details"
            onClick={() => {
              setSelectedRequest({
                ...r,
                item: r?.item || r?.centralCampusInventoryItemId?.itemId
              });
              setDetailsOpen(true);
            }}
            className="shrink-0 px-3 py-1 rounded-[10px] bg-[#8CC9FB] text-black hover:bg-[#7ab8eb] transition text-sm font-medium"
          >
            View Details
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="pt-14 pl-8 pr-12 min-h-screen">
      <div className="flex justify-between items-start">
        <HeaderInfo
          title="Stock Requests"
          desc="Track and manage item requisitions between campuses"
          isSearchBar={true}
          searchPlaceholder="Search requests by item name..."
          isFilters={true}
          showFilterButton={false}
          searchValue={search}
          onSearchChange={debouncedSearch}
          activeFilter={status}
          onFilterChange={(value) => setStatus(value)}
          extraFilters={
            <div className="flex gap-2">
              {isCentral && (
                <select
                  value={requestedCampusId}
                  onChange={(e) => setRequestedCampusId(e.target.value)}
                  className="bg-transparent border border-[#DBD8D8] rounded-[8px] py-1 px-2 text-sm text-[#878686] outline-none min-w-[120px]"
                >
                  <option value="">All Campuses</option>
                  {campusOptions.map((c) => (
                    <option key={c._id} value={c._id}>{c.name}</option>
                  ))}
                </select>
              )}
              <select
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                className="bg-transparent border border-[#DBD8D8] rounded-[8px] py-1 px-2 text-sm text-[#878686] outline-none"
              >
                <option value={10}>10 / page</option>
                <option value={20}>20 / page</option>
                <option value={50}>50 / page</option>
              </select>
            </div>
          }
        />
        <button
          onClick={() => setReportOpen(true)}
          className="flex items-center gap-2 bg-[#279B9F] text-white px-4 py-2 rounded-[8px] text-sm font-medium hover:bg-[#1f7a7d] transition-colors whitespace-nowrap mt-2"
        >
          <FileText size={16} /> View Issued Stock
        </button>
      </div>

      <div className="mt-6 ml-2.5">
        <DataTable
          columns={[
            {
              key: "requested",
              label: "Item Requested",
              className: "col-span-2",
            },
            {
              key: "quantity",
              label: "Quantity",
              className: "col-span-2 text-center ",
            },
            {
              key: "status",
              label: "Current Status",
              className: "col-span-3 text-center",
            },
            {
              key: "timeline",
              label: "Timeline",
              className: "col-span-2 text-center",
            },
            {
              key: "actions",
              label: "Actions",
              className: "col-span-3 text-center",
            },
          ]}
          rows={rows}
          renderRow={renderRow}
          page={page}
          totalPages={totalPages}
          onNext={handleNext}
          onPrev={handlePrev}
        />
      </div>

      <RequestDetailsModal
        open={detailsOpen}
        onClose={() => {
          setDetailsOpen(false);
          setSelectedRequest(null);
        }}
        requestData={selectedRequest || {}}
      />

      <CentralApproveModal
        open={centralApproveOpen}
        onClose={() => setCentralApproveOpen(false)}
        requestData={approveRow}
        isLoading={isCentralApproving}
        onSubmit={async (qty) => {
          try {
            const response = await centralApprovalRequest({
              requestId: approveRow._id,
              status: "approved_central",
              modifiedQuantity: qty,
            }).unwrap();
            if (response?.success === false) {
              toast.error(response?.message || "Failed to approve request");
              return;
            }
            toast.success("Request approved successfully");
            setCentralApproveOpen(false);
          } catch (error) {
            console.error(error);
            toast.error(error?.data?.message || "Failed to approve request");
          }
        }}
      />

      <IssuedStockReportModal 
        open={reportOpen} 
        onClose={() => setReportOpen(false)} 
      />
    </div>
  );
}
